import type { App } from "vue";
import { usePage } from "@inertiajs/vue3";

// Import translation files
import enTranslations from "../lang/en.json";
import csTranslations from "../lang/cs.json";

// Define translations object with 'vue' wrapper to match expected format
const translations = {
    en: { vue: enTranslations },
    cs: { vue: csTranslations },
};

// Get current locale from Inertia page props, fallback to DOM, then default to 'en'
const getCurrentLocale = (): string => {
    try {
        // Try to get from Inertia page props
        const page = usePage();
        if (
            page.props.locale &&
            (page.props.locale === "en" || page.props.locale === "cs")
        ) {
            return page.props.locale;
        }
    } catch (error) {
        // If usePage is not available (e.g., during SSR), fall back to DOM
    }

    // Fallback to DOM
    const htmlElement = document.documentElement;
    const lang =
        htmlElement.getAttribute("lang") ||
        htmlElement.getAttribute("data-lang");

    if (lang && (lang === "en" || lang === "cs")) {
        return lang;
    }

    // Try to get from meta tag
    const metaLang = document
        .querySelector('meta[name="locale"]')
        ?.getAttribute("content");
    if (metaLang && (metaLang === "en" || metaLang === "cs")) {
        return metaLang;
    }

    // Default to 'en'
    return "en";
};

export interface TranslationPlugin {
    $t: (key: string, params?: Record<string, any>) => string;
}

export default {
    install: (app: App) => {
        app.config.globalProperties.$t = function (
            key: string,
            params: Record<string, any> = {},
        ) {
            const locale = getCurrentLocale();
            const currentTranslations =
                translations[locale as keyof typeof translations] ||
                translations.en;

            // Split the key by dots to access nested properties
            const keys = key.split(".");
            let value: any = currentTranslations;

            for (const k of keys) {
                if (value && typeof value === "object" && k in value) {
                    value = value[k];
                } else {
                    // Return the key if translation not found
                    return key;
                }
            }

            // If value is a string, replace parameters
            if (typeof value === "string") {
                Object.keys(params).forEach((param) => {
                    value = value.replace(`{${param}}`, params[param]);
                });
                return value;
            }

            return key;
        };
    },
};
